export function LegalDocumentation() {
  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Essential Legal Documentation for Seller Financing
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Protect your interests with comprehensive legal documents prepared by experienced real estate attorneys
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <div className="bg-gray-50 rounded-lg p-8 shadow-md">
            <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-text-line text-3xl text-white"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Promissory Note</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              The core document outlining the loan terms, payment schedule, interest rate, and consequences of default.
            </p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Principal amount and interest rate</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Payment schedule and due dates</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Late payment penalties</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Prepayment terms</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Default provisions</span>
              </li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-lg p-8 shadow-md">
            <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-home-4-line text-3xl text-white"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Mortgage or Deed of Trust</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Creates the security interest in the property, giving you the legal right to foreclose if buyer defaults.
            </p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Property legal description</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Lien priority and recording</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Foreclosure procedures</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Insurance requirements</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Property maintenance obligations</span>
              </li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-lg p-8 shadow-md">
            <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-contract-line text-3xl text-white"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Purchase Agreement</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              The sales contract outlining all terms and conditions of the property sale and financing arrangement.
            </p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Purchase price and down payment</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Financing terms summary</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Closing date and conditions</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Property condition disclosures</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Contingencies and deadlines</span>
              </li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-lg p-8 shadow-md">
            <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-shield-check-line text-3xl text-white"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Title Insurance Policy</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Protects against title defects, liens, or claims that could affect your security interest in the property.
            </p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Owner's title insurance</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Lender's title insurance</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Title search results</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Lien priority confirmation</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Coverage for legal fees</span>
              </li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-lg p-8 shadow-md">
            <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-shield-line text-3xl text-white"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Property Insurance</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Comprehensive insurance protecting the property with you named as loss payee to protect your investment.
            </p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Property damage coverage</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Liability insurance</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Loss payee designation</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Adequate coverage amounts</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Proof of continuous coverage</span>
              </li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-lg p-8 shadow-md">
            <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-list-3-line text-3xl text-white"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Disclosure Documents</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              Required disclosures about property condition, environmental issues, and other material facts.
            </p>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Property condition disclosure</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Lead paint disclosure (if applicable)</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Environmental hazards</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>Tenant information and leases</span>
              </li>
              <li className="flex items-start">
                <i className="ri-check-line text-lg text-teal-600 mr-2 flex-shrink-0"></i>
                <span>HOA or condo documents</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Why Professional Legal Help Is Essential
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Seller financing involves complex legal and financial arrangements that require <strong>experienced real estate attorneys</strong> to structure properly. Attempting to use generic templates or handle documentation yourself can lead to costly mistakes, unenforceable agreements, and significant legal problems down the road.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Professional attorneys ensure all documents comply with state and federal laws, protect your interests, clearly define all parties' rights and obligations, and provide enforceable remedies if problems arise. The cost of proper legal documentation is minimal compared to the risks of inadequate or improper paperwork.
            </p>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-md">
              <div className="flex items-start">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-alert-line text-2xl text-amber-600"></i>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Important Legal Notice</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Always hire experienced real estate attorneys licensed in your state to prepare and review all seller financing documents. Laws vary significantly by state, and proper documentation is critical to protecting your investment and ensuring enforceability.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/e2d2281b7bef95231a19fb90e8fdc68e.jpg"
              alt="Real estate attorney preparing seller financing legal documents"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg p-8 md:p-12 text-white">
          <h3 className="text-3xl font-bold mb-8 text-center">Document Preparation Checklist</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-start">
                <i className="ri-checkbox-circle-line text-2xl mr-3 flex-shrink-0"></i>
                <span>Hire experienced real estate attorney</span>
              </div>
              <div className="flex items-start">
                <i className="ri-checkbox-circle-line text-2xl mr-3 flex-shrink-0"></i>
                <span>Draft comprehensive promissory note</span>
              </div>
              <div className="flex items-start">
                <i className="ri-checkbox-circle-line text-2xl mr-3 flex-shrink-0"></i>
                <span>Prepare mortgage or deed of trust</span>
              </div>
              <div className="flex items-start">
                <i className="ri-checkbox-circle-line text-2xl mr-3 flex-shrink-0"></i>
                <span>Create detailed purchase agreement</span>
              </div>
              <div className="flex items-start">
                <i className="ri-checkbox-circle-line text-2xl mr-3 flex-shrink-0"></i>
                <span>Obtain title insurance policies</span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start">
                <i className="ri-checkbox-circle-line text-2xl mr-3 flex-shrink-0"></i>
                <span>Verify property insurance with loss payee</span>
              </div>
              <div className="flex items-start">
                <i className="ri-checkbox-circle-line text-2xl mr-3 flex-shrink-0"></i>
                <span>Complete all required disclosures</span>
              </div>
              <div className="flex items-start">
                <i className="ri-checkbox-circle-line text-2xl mr-3 flex-shrink-0"></i>
                <span>Record mortgage/deed of trust</span>
              </div>
              <div className="flex items-start">
                <i className="ri-checkbox-circle-line text-2xl mr-3 flex-shrink-0"></i>
                <span>Set up loan servicing system</span>
              </div>
              <div className="flex items-start">
                <i className="ri-checkbox-circle-line text-2xl mr-3 flex-shrink-0"></i>
                <span>Establish payment tracking procedures</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}